import React from 'react';
import { toast } from 'react-toastify';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { request } from '@mairistem/tools';

import { baseUrlApi } from '@/routes';
import { useEntiteStore } from '@/zustand';

interface VueStatistiqueGraphiqueProps {
  anneeMax: number;
  anneeMin: number;
  setLoading: (loading: boolean) => void;
}

interface StatistiqueAnnuelle {
  recenses?: number;
  recensesRegularises?: number;
  nonRecenses?: number;
  doubleNationalite?: number;
}

type StatistiquesResponse = Record<string, StatistiqueAnnuelle>;

// Ordre custom des éléments dans le tooltip
const tooltipOrder: Record<string, number> = {
  Recenses: 1,
  'Recenses regularises': 2,
  'Non recenses': 3,
  'Double nationalite': 4,
  Total: 5,
};

const getSeriesOrder = (seriesName: string) =>
  tooltipOrder[seriesName] ?? Number.MAX_SAFE_INTEGER;

const VueStatistiqueGraphique = (props: VueStatistiqueGraphiqueProps) => {
  const { anneeMin, anneeMax, setLoading } = props;

  const [statistiques, setStatistiques] =
    React.useState<StatistiquesResponse | null>(null);
  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(`${baseUrlApi}/individus/statistiques/${anneeMin}/${anneeMax}`)
        .then((response: StatistiquesResponse) => {
          setStatistiques(response);
        })
        .catch(() => {
          setStatistiques(null);
          toast.error(
            `Impossible de charger les statistiques entre les années ${anneeMin} et ${anneeMax}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [anneeMin, anneeMax, blocked, setLoading]);

  const data = React.useMemo(() => {
    if (!statistiques || typeof statistiques !== 'object') {
      return [];
    }

    const years = Object.keys(statistiques).sort((a, b) => {
      const yearA = Number(a.replace(/\D/g, ''));
      const yearB = Number(b.replace(/\D/g, ''));

      if (Number.isNaN(yearA) || Number.isNaN(yearB)) {
        return a.localeCompare(b);
      }

      return yearA - yearB;
    });

    return years.map((yearKey) => {
      const yearData = statistiques[yearKey] || {};
      const recenses = yearData.recenses || 0;
      const recensesRegularises = yearData.recensesRegularises || 0;
      const nonRecenses = yearData.nonRecenses || 0;
      const doubleNationalite = yearData.doubleNationalite || 0;
      const total = recenses + recensesRegularises + nonRecenses;

      return {
        annee: yearKey.replace(/^annee/i, ''),
        recenses,
        recensesRegularises,
        nonRecenses,
        doubleNationalite,
        total,
      };
    });
  }, [statistiques]);

  return (
    <div
      style={{
        marginLeft: 0,
        height: '100%',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="annee"
            tick={{ fill: '#666' }}
            tickLine={{ stroke: '#ccc' }}
          />
          <YAxis tick={{ fill: '#666' }} tickLine={{ stroke: '#ccc' }} />
          <Tooltip
            isAnimationActive={false}
            allowEscapeViewBox={{ x: false, y: false }}
            // Ordre custom des éléments dans le tooltip
            itemSorter={(item) => {
              const seriesName = String(
                item.name || item.dataKey || item.value || '',
              );
              return getSeriesOrder(seriesName);
            }}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          />

          {/* Recenses */}
          <Line
            type="monotone"
            dataKey="recenses"
            stroke="#21ba45"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#21ba45', r: 4 }}
            name="Recenses"
            activeDot={{ r: 6 }}
          />

          {/* Recenses regularises */}
          <Line
            type="monotone"
            dataKey="recensesRegularises"
            stroke="#1c79f2"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#1c79f2', r: 4 }}
            name="Recenses regularises"
            activeDot={{ r: 6 }}
          />

          {/* Non recenses */}
          <Line
            type="monotone"
            dataKey="nonRecenses"
            stroke="#db2828"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#db2828', r: 4 }}
            name="Non recenses"
            activeDot={{ r: 6 }}
          />

          {/* Double nationalite */}
          <Line
            type="monotone"
            dataKey="doubleNationalite"
            stroke="#f2711c"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#f2711c', r: 4 }}
            name="Double nationalite"
            activeDot={{ r: 6 }}
          />

          {/* Total */}
          <Line
            type="monotone"
            dataKey="total"
            stroke="#000000"
            strokeWidth={2}
            dot={{ fill: '#000000', r: 4 }}
            name="Total"
            activeDot={{ r: 6 }}
          />
          <Legend
            // Ordre custom des éléments dans la légende
            itemSorter={(item) => {
              if (typeof item === 'string') {
                return getSeriesOrder(item);
              }

              const seriesName = String(item.value || item.dataKey || '');
              return getSeriesOrder(seriesName);
            }}
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="line"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VueStatistiqueGraphique;
